import React from "react";
import { AppProps } from "next/app";

import { createGlobalStyle } from "styled-components";
import {
  newBcaTheme,
  BCAThemeProvider,
  SSRProvider,
  OverlayProvider,
} from "@bca/ui-library";
import Head from "next/head";
import { Provider } from "react-redux";

import { store } from "../redux/store";
import ErrorPage from "../components/common/ErrorPage/ErrorPage";
import { AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import AppInsightsService from "../src/services/AppInsightsService";
import { AuthProvider } from "@bca-auth0/bca-auth0-provider";

const GlobalStyle = createGlobalStyle`
  div#__next, html, body {
    margin: 0;
    min-height: 100%;
    min-width: 100%;
    font-family: "Roboto", "sans-serif";
    overflow-x: hidden;
  }

  textarea {
    font-family: "Roboto", "sans-serif";
  }
`;

interface SalesProps extends AppProps {
  pageProps: {
    user: string;
    idToken: string;
    auth0Domain: string;
    auth0ClientId: string;
    auth0Audience: string;
    auth0Scope: string;
    redirectUri: string;
    appConfigurationUrl: string;
  };
  appInsightsConnectionString: string;
}

export default function Sales({
  Component,
  pageProps,
  appInsightsConnectionString,
}: SalesProps) {
  const {
    user,
    idToken,
    auth0Domain,
    auth0ClientId,
    auth0Audience,
    auth0Scope,
    redirectUri,
  } = pageProps;

  const appInsightsInstance = AppInsightsService.getInstance(
    appInsightsConnectionString
  );
  const reactPlugin = appInsightsInstance.reactPlugin;

  if (typeof window !== "undefined") {
    // @ts-ignore
    window.digitalData = window.digitalData || [];
  }

  return (
    <SSRProvider>
      <AuthProvider
        domain={auth0Domain}
        clientId={auth0ClientId}
        useRefreshTokens
        authorizationParams={{
          redirect_uri: redirectUri,
          audience: auth0Audience,
          scope: auth0Scope,
        }}
      >
        <OverlayProvider>
          <GlobalStyle />
          <BCAThemeProvider theme={newBcaTheme}>
            <AppInsightsErrorBoundary
              onError={() => <ErrorPage user={user} idToken={idToken} />}
              appInsights={reactPlugin}
            >
              <Provider store={store}>
                <Head>
                  <meta
                    name="viewport"
                    content="initial-scale=1.0, width=device-width"
                  />
                </Head>
                <Component {...pageProps} />
              </Provider>
            </AppInsightsErrorBoundary>
          </BCAThemeProvider>
        </OverlayProvider>
      </AuthProvider>
    </SSRProvider>
  );
}

Sales.getInitialProps = async () => {
  return {
    pageProps: {
      auth0Domain: process.env.AUTH0_DOMAIN,
      auth0ClientId: process.env.AUTH0_CLIENT_ID,
      auth0Audience: process.env.AUTH0_AUDIENCE,
      auth0Scope: process.env.AUTH0_SCOPE,
      redirectUri: process.env.AUTH_PROVIDER_REDIRECT_URI,
    },
    appInsightsConnectionString:
      process.env.APPLICATIONINSIGHTS_CONNECTION_STRING,
  };
};
